import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import type { ColumnDef } from "@tanstack/react-table";
import { getImpersonationGetImpersonationLogsQueryKey, impersonationGetImpersonationLogs } from "@/orval/impersonation";
import type { ImpersonationLogDTO } from "@/orval/model";
import { UserCheck } from "lucide-react";
import { ImpersonationDialog } from "./-components/impersonation-dialog";
import { PortalPageHeader } from "@/components/portal-page-header";
import { parseISO } from "date-fns";

function ImpersonationPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    fullName: string;
    email: string;
  } | null>(null);

  const getImpersonationLogs = impersonationGetImpersonationLogs;

  const handleStartImpersonation = (user: { id: number; firstName: string; lastName: string; email: string }) => {
    setSelectedUser({
      id: user.id,
      fullName: `${user.firstName} ${user.lastName}`,
      email: user.email,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
  };

  // Define columns for the audit logs table
  const auditLogColumns: ColumnDef<ImpersonationLogDTO, never>[] = [
    {
      accessorKey: "siteAdmin.email",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Site Admin" />,
      enableSorting: true,
      cell: ({ row }) => {
        const admin = row.original.siteAdmin;
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {admin.firstName} {admin.lastName}
            </span>
            <span className="text-sm text-muted-foreground">{admin.email}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "targetUser.email",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Target User" />,
      enableSorting: true,
      cell: ({ row }) => {
        const user = row.original.targetUser;
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {user.firstName} {user.lastName}
            </span>
            <span className="text-sm text-muted-foreground">{user.email}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "action",
      header: "Action",
      enableSorting: false,
      cell: ({ getValue }) => {
        const action = getValue() as string;
        const variant = action === "Started" ? "default" : "secondary";
        return <Badge variant={variant}>{action}</Badge>;
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date/Time" />,
      enableSorting: true,
      cell: ({ getValue }) => {
        const date = parseISO(getValue() as string);
        return (
          <div className="flex flex-col">
            <span>{date.toLocaleDateString()}</span>
            <span className="text-sm text-muted-foreground">{date.toLocaleTimeString()}</span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original.targetUser;
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleStartImpersonation(user)}
            className="flex items-center gap-1"
          >
            <UserCheck className="size-3" />
            Impersonate
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-1 min-h-0 flex-col p-4 md:p-6">
      <PortalPageHeader
        className="mb-8"
        title="User Impersonation"
        actions={
          <Button onClick={() => setIsDialogOpen(true)} className="flex items-center gap-2">
            <UserCheck className="size-4" />
            Start Impersonation
          </Button>
        }
      />

      <DataTable
        columns={auditLogColumns}
        queryFn={(params, abortSignal) => getImpersonationLogs(params, abortSignal)}
        queryKeyFn={(params) => getImpersonationGetImpersonationLogsQueryKey(params)}
        getRowIdFn={(row) => row.id.toString()}
      />

      <ImpersonationDialog isOpen={isDialogOpen} onClose={handleCloseDialog} preSelectedUser={selectedUser} />
    </div>
  );
}

export const Route = createFileRoute("/_app/site-admin/impersonation")({
  component: ImpersonationPage,
});
